import React, { useState, useEffect } from "react";
import {
  FiUsers,
  FiAlertCircle,
  FiCreditCard,
  FiBriefcase,
  FiPlus,
  FiTrash2,
  FiSliders,
  FiCheck,
} from "react-icons/fi";
import Modal from "../common/Modal";
import ButtonLoading from "../ButtonLoading";
import FundSelector from "./FundSelector";
import { userRequest } from "../../utils/requestMethods";

/**
 * Sub-modal for configuring bonuses & deductions for a single staff member.
 */
const StaffAdjustmentSubModal = ({ isOpen, onClose, member, adjustments = [], onSave }) => {
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    if (isOpen) {
      setItems(adjustments.map((a) => ({ ...a })));
      setError("");
    }
  }, [isOpen, adjustments]);

  if (!isOpen || !member) return null;

  const baseSalary = Number(member.metadata?.monthly_salary || 0);
  const bonuses = items
    .filter((i) => i.type === "bonus")
    .reduce((sum, i) => sum + (Number(i.amount) || 0), 0);
  const deductions = items
    .filter((i) => i.type === "deduction")
    .reduce((sum, i) => sum + (Number(i.amount) || 0), 0);
  const netPay = baseSalary + bonuses - deductions;

  const addItem = () => {
    setItems((prev) => [...prev, { type: "bonus", label: "", amount: "" }]);
  };

  const removeItem = (idx) => {
    setItems((prev) => prev.filter((_, i) => i !== idx));
  };

  const updateItem = (idx, field, val) => {
    setItems((prev) => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], [field]: val };
      return updated;
    });
  };

  const handleSave = () => {
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (!item.label.trim()) {
        setError(`Adjustment #${i + 1} requires a label/reason (e.g. Overtime).`);
        return;
      }
      if (!item.amount || Number(item.amount) <= 0) {
        setError(`Adjustment #${i + 1} amount must be greater than 0.`);
        return;
      }
    }

    if (netPay < 0) {
      setError(
        `Net pay (₦${netPay.toLocaleString()}) cannot be negative. Deductions exceed base salary + bonuses.`,
      );
      return;
    }

    onSave(items.map((i) => ({ type: i.type, label: i.label.trim(), amount: Number(i.amount) })));
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Adjust Salary — ${member.firstname} ${member.lastname}`}
      icon={<FiSliders className="text-indigo-600 text-lg" />}
      maxWidth="max-w-lg"
    >
      {error && (
        <div className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 rounded-xl px-3 py-2.5 text-xs mb-3">
          <FiAlertCircle className="mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <div className="space-y-4 text-sm">
        {/* Base Salary Overview */}
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 flex items-center justify-between">
          <span className="text-gray-600 font-medium">Base Monthly Salary:</span>
          <span className="font-bold text-gray-900">₦{baseSalary.toLocaleString()}</span>
        </div>

        {/* Adjustments List */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">
              Bonuses & Deductions
            </label>
            <button
              type="button"
              onClick={addItem}
              className="inline-flex items-center gap-1 text-xs font-semibold text-indigo-600 hover:text-indigo-800 transition-colors"
            >
              <FiPlus className="w-3.5 h-3.5" /> Add Adjustment
            </button>
          </div>

          {items.length === 0 ? (
            <div className="text-center py-6 border border-dashed border-gray-200 rounded-xl text-gray-400 text-xs">
              No adjustments added yet. Click "+ Add Adjustment" to include overtime, bonuses, or
              deductions.
            </div>
          ) : (
            <div className="space-y-2.5 max-h-48 overflow-y-auto pr-1">
              {items.map((item, idx) => (
                <div
                  key={idx}
                  className="flex items-center gap-2 bg-gray-50 p-2.5 rounded-xl border border-gray-200"
                >
                  <select
                    value={item.type}
                    onChange={(e) => updateItem(idx, "type", e.target.value)}
                    className="rounded-lg bg-white border border-gray-200 px-2 py-1.5 text-xs font-medium text-gray-800 focus:outline-none focus:border-indigo-500"
                  >
                    <option value="bonus">+ Bonus</option>
                    <option value="deduction">- Deduction</option>
                  </select>
                  <input
                    type="text"
                    placeholder="Reason / Label (e.g. Overtime)"
                    value={item.label}
                    onChange={(e) => updateItem(idx, "label", e.target.value)}
                    className="flex-1 rounded-lg bg-white border border-gray-200 px-2.5 py-1.5 text-xs focus:outline-none focus:border-indigo-500"
                  />
                  <input
                    type="number"
                    placeholder="Amount (₦)"
                    value={item.amount}
                    onChange={(e) => updateItem(idx, "amount", e.target.value)}
                    className="w-28 rounded-lg bg-white border border-gray-200 px-2.5 py-1.5 text-xs focus:outline-none focus:border-indigo-500"
                  />
                  <button
                    type="button"
                    onClick={() => removeItem(idx)}
                    className="p-1.5 text-gray-400 hover:text-red-600 rounded-lg hover:bg-gray-200/60 transition-colors"
                    title="Delete"
                  >
                    <FiTrash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Calculation Breakdown */}
        <div className="border-t border-gray-200 pt-3 space-y-1 text-xs">
          <div className="flex justify-between text-gray-600">
            <span>Base Salary</span>
            <span>₦{baseSalary.toLocaleString()}</span>
          </div>
          {bonuses > 0 && (
            <div className="flex justify-between text-emerald-600 font-medium">
              <span>+ Total Bonuses</span>
              <span>+₦{bonuses.toLocaleString()}</span>
            </div>
          )}
          {deductions > 0 && (
            <div className="flex justify-between text-red-600 font-medium">
              <span>- Total Deductions</span>
              <span>-₦{deductions.toLocaleString()}</span>
            </div>
          )}
          <div className="flex justify-between text-sm font-bold text-indigo-700 border-t border-gray-100 pt-2 mt-1">
            <span>Computed Net Pay</span>
            <span>₦{netPay.toLocaleString()}</span>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="w-1/3 py-2 px-4 border border-gray-200 text-gray-700 text-xs font-semibold rounded-full hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="w-2/3 py-2 px-4 bg-indigo-600 text-white text-xs font-semibold rounded-full hover:bg-indigo-700 transition-colors inline-flex items-center justify-center gap-1"
          >
            <FiCheck className="w-3.5 h-3.5" /> Save Adjustments
          </button>
        </div>
      </div>
    </Modal>
  );
};

const CreateSalaryBatchModal = ({ isOpen, onClose, schoolId, onCreated }) => {
  const [staff, setStaff] = useState([]);
  const [fundId, setFundId] = useState("");
  const [paymentMonth, setPaymentMonth] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [adjustmentsMap, setAdjustmentsMap] = useState({}); // { [staffId]: [{ type: 'bonus'|'deduction', label: '', amount: '' }] }
  const [adjustingMember, setAdjustingMember] = useState(null); // staff member currently open in sub-modal
  const [generatingWalletId, setGeneratingWalletId] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [payoutMode, setPayoutMode] = useState("bank_account");

  const handleGenerateWallet = async (member, e) => {
    e?.stopPropagation();
    setGeneratingWalletId(member._id);
    setError("");
    try {
      const res = await userRequest.post("/banking/virtual-accounts/staff/bulk", {
        schoolId,
        staff: [{ staffId: member._id, staffName: `${member.firstname} ${member.lastname}` }],
        createWalletIfNotExists: true,
      });
      const resultItem = res.data?.results?.[0] || res.data;
      const generatedAcc =
        resultItem?.accountNumber ||
        resultItem?.account_number ||
        res.data?.accountNumber ||
        "GENERATED";
      setStaff((prev) =>
        prev.map((s) =>
          s._id === member._id
            ? { ...s, account_number: generatedAcc, account_id: generatedAcc }
            : s,
        ),
      );
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to generate staff virtual wallet.");
    } finally {
      setGeneratingWalletId(null);
    }
  };

  useEffect(() => {
    if (!isOpen || !schoolId) return;
    const fetchStaff = async () => {
      try {
        const [teachersRes, adminsRes] = await Promise.all([
          userRequest.get(`/teachers?school_id=${schoolId}&limit=100`),
          userRequest.get(`/schools/${schoolId}/admins?limit=100`),
        ]);
        const teachers = (teachersRes.data.teachers || []).map((t) => ({
          ...t,
          roleLabel: "Teacher",
        }));
        const admins = (adminsRes.data.users || []).map((a) => ({
          ...a,
          roleLabel: a.role === 2 ? "Burser" : "Admin",
        }));
        setStaff([...teachers, ...admins]);
      } catch (err) {
        console.log(err);
      }
    };
    fetchStaff();
  }, [isOpen, schoolId]);

  const toggleStaff = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const reset = () => {
    setFundId("");
    setPaymentMonth("");
    setSelectedIds([]);
    setAdjustmentsMap({});
    setAdjustingMember(null);
    setError("");
    setPayoutMode("bank_account");
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const getStaffStatus = (member) => {
    const hasSalary = Number(member.metadata?.monthly_salary) > 0;
    const cp = member.counter_party;
    const hasBank = Boolean(cp?.account_number?.trim() && cp?.bank_code?.trim());
    const hasWallet = Boolean(member.account_number?.trim() || member.account_id?.trim());
    const isReady = payoutMode === "bank_account" ? hasSalary && hasBank : hasSalary && hasWallet;
    return {
      hasSalary,
      hasBank,
      hasWallet,
      isReady,
      monthlySalary: Number(member.metadata?.monthly_salary || 0),
      bankName: cp?.bank_name || "Bank",
      accountNumber: cp?.account_number,
    };
  };

  const computeStaffNetPay = (member) => {
    const base = Number(member.metadata?.monthly_salary || 0);
    const adjustments = adjustmentsMap[member._id] || [];
    const bonuses = adjustments
      .filter((a) => a.type === "bonus")
      .reduce((sum, a) => sum + (Number(a.amount) || 0), 0);
    const deductions = adjustments
      .filter((a) => a.type === "deduction")
      .reduce((sum, a) => sum + (Number(a.amount) || 0), 0);
    return { base, bonuses, deductions, netPay: base + bonuses - deductions };
  };

  const selectAllReady = () => {
    const readyIds = staff.filter((m) => getStaffStatus(m).isReady).map((m) => m._id);
    setSelectedIds(readyIds);
  };

  const submitHandler = async (e) => {
    e.preventDefault();
    if (!fundId || !paymentMonth || selectedIds.length === 0) {
      setError("Please select a fund, payment month, and at least one staff member.");
      return;
    }

    const staffPayload = [];
    for (const id of selectedIds) {
      const member = staff.find((m) => m._id === id);
      const { netPay } = computeStaffNetPay(member);
      if (netPay < 0) {
        setError(
          `Net pay for ${member.firstname} ${member.lastname} cannot be negative (₦${netPay.toLocaleString()}). Deductions exceed base salary + bonuses.`,
        );
        return;
      }

      staffPayload.push({
        staff_id: id,
        adjustments: adjustmentsMap[id] || [],
      });
    }

    setLoading(true);
    setError("");
    try {
      const response = await userRequest.post(`/schools/${schoolId}/salary-batches`, {
        fund_id: fundId,
        payment_month: paymentMonth,
        staff: staffPayload,
        payout_mode: payoutMode,
      });
      onCreated?.(response.data?.batch);
      reset();
      onClose();
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to create salary batch. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const readyCount = staff.filter((m) => getStaffStatus(m).isReady).length;

  const totalBatchAmount = selectedIds.reduce((sum, id) => {
    const member = staff.find((m) => m._id === id);
    if (!member) return sum;
    const { netPay } = computeStaffNetPay(member);
    return sum + (netPay > 0 ? netPay : 0);
  }, 0);

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title="New Salary Batch"
        icon={<FiUsers className="text-indigo-600 text-lg" />}
        maxWidth="max-w-xl"
      >
        {error && (
          <div className="flex items-start gap-3 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm mb-4">
            <FiAlertCircle className="mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={submitHandler} className="space-y-4">
          <FundSelector
            schoolId={schoolId}
            value={fundId}
            onChange={setFundId}
            label="Source Fund"
          />

          <div>
            <label className="block mb-1.5 text-sm font-medium text-gray-700">Payment Month</label>
            <input
              type="month"
              value={paymentMonth}
              onChange={(e) => setPaymentMonth(e.target.value)}
              className="w-full rounded-xl bg-gray-50 border border-gray-200 px-4 py-2.5 text-sm focus:border-indigo-500 focus:ring-indigo-500 focus:outline-none"
            />
          </div>

          {/* Payout Mode */}
          <div>
            {/* <label className="block mb-1.5 text-sm font-medium text-gray-700">Payout Mode</label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => {
                  setPayoutMode("bank_account");
                  setSelectedIds([]);
                }}
                className={`flex items-center gap-2 px-4 py-3 rounded-xl border text-sm font-medium transition-all ${
                  payoutMode === "bank_account"
                    ? "border-indigo-500 bg-indigo-50 text-indigo-700 ring-1 ring-indigo-500"
                    : "border-gray-200 bg-gray-50 text-gray-600 hover:border-gray-300"
                }`}
              >
                <FiCreditCard className="w-4 h-4" />
                Bank Account
              </button>
              <button
                type="button"
                onClick={() => { setPayoutMode('staff_wallet'); setSelectedIds([]); }}
                className={`flex items-center gap-2 px-4 py-3 rounded-xl border text-sm font-medium transition-all ${
                  payoutMode === 'staff_wallet'
                    ? 'border-indigo-500 bg-indigo-50 text-indigo-700 ring-1 ring-indigo-500'
                    : 'border-gray-200 bg-gray-50 text-gray-600 hover:border-gray-300'
                }`}
              >
                <FiBriefcase className="w-4 h-4" />
                Staff Wallet
              </button>
            </div> */}
            <p className="text-xs text-gray-400 mt-1.5">
              {payoutMode === "bank_account"
                ? "Salaries will be sent via NIP transfer to each staff member\u2019s bank account."
                : "Salaries will be credited to each staff member\u2019s Acceede virtual wallet."}
            </p>
          </div>

          {/* Staff Selection & Adjustments */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-sm font-medium text-gray-700">
                Staff Members ({selectedIds.length} selected)
              </label>
              {readyCount > 0 && (
                <button
                  type="button"
                  onClick={selectAllReady}
                  className="text-xs font-semibold text-indigo-600 hover:text-indigo-800 transition-colors"
                >
                  Select All Ready ({readyCount})
                </button>
              )}
            </div>

            <div className="max-h-64 overflow-y-auto rounded-xl border border-gray-200 divide-y divide-gray-100">
              {staff.length === 0 ? (
                <p className="px-4 py-3 text-sm text-gray-400">No eligible staff found.</p>
              ) : (
                staff.map((member) => {
                  const status = getStaffStatus(member);
                  const isChecked = selectedIds.includes(member._id);
                  const memberAdjustments = adjustmentsMap[member._id] || [];
                  const { netPay } = computeStaffNetPay(member);

                  return (
                    <div
                      key={member._id}
                      className={`flex items-center justify-between gap-3 px-4 py-3 text-sm transition-colors ${
                        !status.isReady
                          ? "opacity-60 bg-gray-50/50 cursor-not-allowed"
                          : "hover:bg-gray-50"
                      }`}
                    >
                      <label className="flex items-center gap-3 cursor-pointer flex-1 min-w-0">
                        <input
                          type="checkbox"
                          disabled={!status.isReady}
                          checked={isChecked}
                          onChange={() => toggleStaff(member._id)}
                          className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 disabled:opacity-50 shrink-0"
                        />
                        <div className="truncate">
                          <p className="font-medium text-gray-800 truncate">
                            {member.firstname} {member.lastname}
                          </p>
                          <p className="text-xs text-gray-400">{member.roleLabel}</p>
                        </div>
                      </label>

                      <div className="flex items-center gap-2 shrink-0">
                        {status.isReady ? (
                          <>
                            <span className="text-xs font-semibold text-gray-700">
                              ₦{netPay.toLocaleString()}
                            </span>
                            {isChecked && (
                              <button
                                type="button"
                                onClick={() => setAdjustingMember(member)}
                                className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full font-medium transition-colors ${
                                  memberAdjustments.length > 0
                                    ? "bg-indigo-100 text-indigo-700 border border-indigo-200 hover:bg-indigo-200"
                                    : "bg-gray-100 text-gray-600 border border-gray-200 hover:bg-gray-200"
                                }`}
                              >
                                <FiSliders className="w-3 h-3" />
                                {memberAdjustments.length > 0
                                  ? `${memberAdjustments.length} Adjustments`
                                  : "+ Adjustment"}
                              </button>
                            )}
                          </>
                        ) : !status.hasSalary && !status.hasBank ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-red-50 text-red-700 border border-red-200">
                            No Salary & Bank
                          </span>
                        ) : !status.hasSalary ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                            No Salary Set
                          </span>
                        ) : payoutMode === "bank_account" && !status.hasBank ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                            Missing Bank Account
                          </span>
                        ) : payoutMode === "staff_wallet" && !status.hasWallet ? (
                          <div className="flex items-center gap-1.5">
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                              Missing Staff Wallet
                            </span>
                            <button
                              type="button"
                              disabled={generatingWalletId === member._id}
                              onClick={(e) => handleGenerateWallet(member, e)}
                              className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full font-semibold text-indigo-700 bg-indigo-50 border border-indigo-200 hover:bg-indigo-100 transition-colors disabled:opacity-50"
                            >
                              {generatingWalletId === member._id
                                ? "Generating..."
                                : "+ Generate Wallet"}
                            </button>
                          </div>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                            Not Ready
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {selectedIds.length > 0 && (
            <div className="flex items-center justify-between p-3 bg-indigo-50/60 rounded-xl border border-indigo-100 text-sm">
              <span className="font-medium text-indigo-900">Total Payroll Amount:</span>
              <span className="font-bold text-indigo-700 text-base">
                ₦{totalBatchAmount.toLocaleString()}
              </span>
            </div>
          )}

          {loading ? (
            <ButtonLoading styles="w-full text-white bg-indigo-500 font-medium rounded-full text-sm px-5 py-2.5 text-center" />
          ) : (
            <button
              type="submit"
              className="w-full text-white bg-indigo-600 hover:bg-indigo-700 font-medium rounded-full text-sm px-5 py-2.5 text-center transition-colors"
            >
              Create Batch
            </button>
          )}
        </form>
      </Modal>

      {/* Sub-modal for editing adjustments of a specific staff member */}
      <StaffAdjustmentSubModal
        isOpen={Boolean(adjustingMember)}
        onClose={() => setAdjustingMember(null)}
        member={adjustingMember}
        adjustments={adjustingMember ? adjustmentsMap[adjustingMember._id] || [] : []}
        onSave={(newAdjustments) => {
          if (adjustingMember) {
            setAdjustmentsMap((prev) => ({
              ...prev,
              [adjustingMember._id]: newAdjustments,
            }));
          }
        }}
      />
    </>
  );
};

export default CreateSalaryBatchModal;
